import { FormEvent, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { SlideOver } from '@/components/SlideOver.js';
import { assignTicket, updateTicket, type TicketRecord } from '@/lib/api/tickets.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { prettyLabel } from '@/lib/format.js';

function toDateTimeLocal(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const offsetMs = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function parseTagsInput(raw: string): string[] {
  return raw
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function tagsToInput(tags?: string[] | null): string {
  return tags?.join(', ') ?? '';
}

export function TicketEditSlideOver({
  ticket,
  users,
  open,
  onClose,
  onSaved,
}: {
  ticket: TicketRecord;
  users: Array<{ id: string; firstName: string; lastName: string }>;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [customerComplaint, setCustomerComplaint] = useState('');
  const [priority, setPriority] = useState('normal');
  const [promisedDate, setPromisedDate] = useState('');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  useEffect(() => {
    if (!open) return;
    setCustomerComplaint(ticket.customerComplaint ?? '');
    setPriority(ticket.priority);
    setPromisedDate(toDateTimeLocal(ticket.promisedDate));
    setIntakeNotes(ticket.intakeNotes ?? '');
    setTagsInput(tagsToInput(ticket.tags));
    setAssignedTo(ticket.assignedTo ?? '');
  }, [open, ticket]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};

      if (customerComplaint !== (ticket.customerComplaint ?? '')) {
        body.customerComplaint = customerComplaint.trim().slice(0, 500) || null;
      }

      if (priority !== ticket.priority) {
        body.priority = priority;
      }

      const newPromisedMs = promisedDate ? new Date(promisedDate).getTime() : null;
      const oldPromisedMs = ticket.promisedDate ? new Date(ticket.promisedDate).getTime() : null;
      if (newPromisedMs !== oldPromisedMs) {
        body.promisedDate = promisedDate ? new Date(promisedDate).toISOString() : null;
      }

      if (intakeNotes !== (ticket.intakeNotes ?? '')) {
        body.intakeNotes = intakeNotes.trim() || null;
      }

      const newTags = parseTagsInput(tagsInput);
      const oldTags = ticket.tags ?? [];
      if (JSON.stringify(newTags) !== JSON.stringify(oldTags)) {
        body.tags = newTags;
      }

      const tasks: Promise<unknown>[] = [];
      if (Object.keys(body).length > 0) {
        tasks.push(updateTicket(ticket.id, body));
      }
      if (assignedTo && assignedTo !== (ticket.assignedTo ?? '')) {
        tasks.push(assignTicket(ticket.id, { technicianId: assignedTo }));
      }

      if (tasks.length === 0) return { unchanged: true as const };
      await Promise.all(tasks);
      return { unchanged: false as const };
    },
    onSuccess: (result) => {
      if (result?.unchanged) {
        onClose();
        return;
      }
      toastSuccess('Ticket updated');
      onSaved();
      onClose();
    },
    onError: (error) => {
      const parsed = parseApiError(error);
      toastError(parsed.message, parsed.code);
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    saveMutation.mutate();
  }

  return (
    <SlideOver title={`Edit ${ticket.ticketNumber}`} open={open} onClose={onClose} panelClassName="max-w-lg">
      <form className="flex h-full flex-col" onSubmit={submit}>
        <div className="flex-1 space-y-4">
          <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
            <p>
              <span className="font-semibold text-slate-800">Ticket:</span> {ticket.ticketNumber}
            </p>
            <p className="mt-1">
              <span className="font-semibold text-slate-800">Created:</span>{' '}
              {new Date(ticket.createdAt).toLocaleString()}
            </p>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Customer complaint / issue</span>
            <textarea
              className="mt-1 min-h-24 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={customerComplaint}
              onChange={(e) => setCustomerComplaint(e.target.value.slice(0, 500))}
              placeholder="Describe the customer's issue…"
              maxLength={500}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Priority</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm capitalize"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            >
              {['normal', 'rush', 'urgent'].map((p) => (
                <option key={p} value={p}>
                  {p === 'rush' ? '⚡ Rush' : prettyLabel(p)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Promised date</span>
            <input
              type="datetime-local"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={promisedDate}
              onChange={(e) => setPromisedDate(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Intake notes</span>
            <textarea
              className="mt-1 min-h-20 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              value={intakeNotes}
              onChange={(e) => setIntakeNotes(e.target.value)}
              placeholder="Internal intake notes…"
            />
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Tags</span>
            <input
              type="text"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="vip, warranty, repeat-customer"
            />
            <span className="mt-1 block text-xs text-slate-500">Comma-separated</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800">Assigned technician</span>
            <select
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              <option value="">Unassigned</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-6 flex gap-2 border-t border-slate-200 pt-4">
          <button
            type="button"
            className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            onClick={onClose}
            disabled={saveMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95 disabled:opacity-50"
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </SlideOver>
  );
}
